import React, { useState, useEffect } from "react";
import PubNub from "pubnub";
import { PubNubProvider, usePubNub } from "pubnub-react";
import "./App.css";
import { getConfigValues } from "./config/azureConfig";
import { CONFIG_KEYS, FALLBACK_CONFIG } from "./config/appConfig";

// Initialize PubNub with configuration from Azure App Configuration
async function initializePubNub() {
  // Fetch config with fallback values
  const config = await getConfigValues({
    [CONFIG_KEYS.PUBNUB_PUBLISH_KEY]: FALLBACK_CONFIG[CONFIG_KEYS.PUBNUB_PUBLISH_KEY],
    [CONFIG_KEYS.PUBNUB_SUBSCRIBE_KEY]: FALLBACK_CONFIG[CONFIG_KEYS.PUBNUB_SUBSCRIBE_KEY],
    [CONFIG_KEYS.PUBNUB_CHANNEL]: FALLBACK_CONFIG[CONFIG_KEYS.PUBNUB_CHANNEL],
  });

  const pubnub = new PubNub({
    publishKey: config[CONFIG_KEYS.PUBNUB_PUBLISH_KEY],
    subscribeKey: config[CONFIG_KEYS.PUBNUB_SUBSCRIBE_KEY],
    uuid: `user-${Math.random().toString(36).substring(7)}`,
  });

  // Return both pubnub instance and channel
  return {
    client: pubnub,
    channel: config[CONFIG_KEYS.PUBNUB_CHANNEL],
  };
}

function PubNubDemo({ channel }) {
  const pubnub = usePubNub();
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (!pubnub || !channel) return;

    // Subscribe to the channel
    pubnub.subscribe({ channels: [channel] });

    // Listen for messages
    const listener = {
      message: (messageEvent) => {
        if (messageEvent.message.type === "checkbox-update") {
          setIsChecked(messageEvent.message.checked);
        }
      },
    };

    pubnub.addListener(listener);

    // Cleanup on unmount
    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({ channels: [channel] });
    };
  }, [pubnub, channel]);

  const handleCheckboxChange = (e) => {
    if (!pubnub) return;

    const newCheckedState = e.target.checked;
    setIsChecked(newCheckedState);

    // Publish the change to all subscribed instances
    pubnub.publish({
      channel: channel,
      message: {
        type: "checkbox-update",
        checked: newCheckedState,
        timestamp: new Date().toISOString(),
      },
    });
  };

  const openNewWindow = () => {
    window.open(window.location.href, "_blank", "width=600,height=400");
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>PubNub Demo</h1>

        <div className="demo-container">
          <div className="checkbox-container">
            <label>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
              />
              <span className="checkbox-label">Checkbox Control</span>
            </label>
          </div>

          <button className="open-window-btn" onClick={openNewWindow}>
            Open New Window
          </button>

          <div className="info">
            <p>Status: {isChecked ? "Checked ✓" : "Unchecked"}</p>
            <p className="instructions">
              Click the button above to open another window. Toggle the checkbox
              in any window to see it sync across all instances!
            </p>
          </div>
        </div>
      </header>
    </div>
  );
}

function App() {
  const [client, setClient] = useState(null);
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializePubNub()
      .then(({ client: initializedClient, channel: configuredChannel }) => {
        setClient(initializedClient);
        setChannel(configuredChannel);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to initialize PubNub:", err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="App">
        <header className="App-header">
          <p>Loading configuration...</p>
        </header>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <header className="App-header">
          <p>Error loading configuration: {error}</p>
        </header>
      </div>
    );
  }

  return (
    <PubNubProvider client={client}>
      <PubNubDemo channel={channel} />
    </PubNubProvider>
  );
}

export default App;
